// src/redux/slices/drawers/controlled_substance_logging/containerLogDrawerSlice.ts
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { csl, IAppointmentLite, patients } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { RootState } from "src/redux/store";
import {
    ECSLCheckoutStatus,
    ECSLLogType,
    ECSLSignatureRequestType,
    ESearchTableType,
} from "src/types/global_enums";
import { generateUUID, handleError, notify } from "src/utils";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";

export interface IContainerLogDrawerState extends IBaseDrawerState {
    // Drawer state
    show: boolean;
    patientId?: number;
    appointmentId?: number;
    onLogEntrySave?: () => void;

    // Details state
    containerLogId?: number;
    logDetails?: csl.IContainerLogFullDetails;

    isLoading: boolean;
    error: string | null;

    // New state for log entry
    selectedPatient?: patients.IAccountPatientSearch;
    selectedAppointment?: IAppointmentLite;
    selectedMedication?: csl.INdcVariantFullDetails;
    selectedContainer?: csl.IContainerFullDetails;
    dateOfUse?: Date;
    timeOfUse?: string;
    amountRemoved?: number;
    isAdministered?: boolean; // vs dispensed
    primaryLogOption?: csl.ILogEntryOption;
    secondaryLogOption?: csl.ILogEntryOption;
    comments?: string;

    // Add checkout related state
    checkoutStatus?: ECSLCheckoutStatus;
    checkoutMessage?: string;
    isSubmittingCheckOut: boolean;
    checkoutError: string | null;

    // Signature state
    primarySignee?: csl.ISignatureRequestSigner;
    signatureBlob?: string;
    signedDateTimeISO?: string;
    isSubmitting: boolean;

    showWastingCard: boolean;
    wastingAmount?: number;
    wastingMethodOption?: csl.ILogEntryOption;
    wastingReasonOption?: csl.ILogEntryOption;
    wastingComments?: string;

    // witness signature state
    witnessSignee?: csl.ISignatureRequestSigner;
    witnessSignatureBlob?: string;
    witnessSignedDateTimeISO?: string;

    // Signing Existing Log Entry state
    signatureRequest?: csl.ISignatureRequest;
    isSignatureMode: boolean;
    isSignatureSubmitted: boolean;
}

export const initialStateContainerLogDrawer: IContainerLogDrawerState = {
    id: "containerLogDrawer",
    show: false,
    isLoading: false,
    error: null,

    // new log entry state
    selectedPatient: undefined,
    selectedAppointment: undefined,
    selectedMedication: undefined,
    selectedContainer: undefined,
    dateOfUse: undefined,
    timeOfUse: undefined,
    amountRemoved: undefined,
    isAdministered: undefined,
    primaryLogOption: undefined,
    secondaryLogOption: undefined,
    comments: "",

    // checkout state
    checkoutStatus: undefined,
    checkoutMessage: undefined,
    isSubmittingCheckOut: false,
    checkoutError: null,

    primarySignee: undefined,
    signatureBlob: undefined,
    signedDateTimeISO: undefined,
    isSubmitting: false,

    showWastingCard: false,
    wastingAmount: undefined,
    wastingMethodOption: undefined,
    wastingReasonOption: undefined,
    wastingComments: "",
    witnessSignee: undefined,
    witnessSignatureBlob: undefined,
    witnessSignedDateTimeISO: undefined,

    signatureRequest: undefined,
    isSignatureMode: false,
    isSignatureSubmitted: false,
};

export const openContainerLogDrawer = createAsyncThunk<
    csl.IContainerLogFullDetails,
    { containerLogId: number },
    { rejectValue: string }
>("containerLogDrawer/openContainerLogDrawer", async (params, { rejectWithValue }) => {
    if (params.containerLogId) {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/${params.containerLogId}/details`;
            const { data } = await axios.get(route, { withCredentials: true });
            return data;
        } catch (error: any) {
            notify({ level: "error", title: "Error", message: "Failed to load log entry details" });
            return rejectWithValue(error?.message || "Failed to load log entry details");
        }
    }
    return rejectWithValue("No containerLogId provided");
});

export const initializeLogEntryWithAppointment = createAsyncThunk<
    { appointment: IAppointmentLite; patient: patients.IAccountPatientSearch },
    number,
    { rejectValue: string }
>(
    "containerLogDrawer/initializeLogEntryWithAppointment",
    async (appointmentId: number, { rejectWithValue }) => {
        try {
            // First fetch the appointment details
            const appointmentRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/search/${appointmentId}`;
            const appointmentResponse = await axios.get<IAppointmentLite>(appointmentRoute, {
                withCredentials: true,
            });
            const appointment = appointmentResponse.data;
            // Then fetch the patient details
            const patientRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/patients/search_v2/${appointment.accountPatientId}`;
            const patientResponse = await axios.get<patients.IAccountPatientSearch>(patientRoute, {
                withCredentials: true,
            });
            return { appointment: appointment, patient: patientResponse.data };
        } catch (error: any) {
            notify({
                level: "error",
                title: "Error",
                message:
                    "Failed to load appointment and patient details when opening up the container log entry drawer",
            });
            return rejectWithValue(
                error?.message || "Failed to load appointment and patient details",
            );
        }
    },
);

export const startContainerLogSignature = createAsyncThunk<
    csl.ISignatureRequestData,
    number,
    { rejectValue: string }
>(
    "containerLogDrawer/startContainerLogSignature",
    async (signatureRequestId: number, { rejectWithValue }) => {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/${signatureRequestId}/related-container-logs`;
            const { data } = await axios.get<csl.ISignatureRequestData>(route, {
                withCredentials: true,
            });
            if (!data?.containerLogs?.length) {
                notify({
                    level: "error",
                    title: "Error",
                    message: "No container entries found for this signature request",
                });
                return rejectWithValue("No container entries found");
            }
            return data;
        } catch (error) {
            notify({
                level: "error",
                title: "Error",
                message: "Failed to load signature request details",
            });
            return rejectWithValue("Failed to load signature request details");
        }
    },
);

export const checkoutContainer = createAsyncThunk<
    csl.IContainerCheckout,
    { containerId: number },
    { rejectValue: string }
>("containerLogDrawer/checkoutContainer", async (params, { rejectWithValue }) => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/${params.containerId}/checkout`;
        const { data } = await axios.post(route, {}, { withCredentials: true });
        return data as csl.IContainerCheckout;
    } catch (error: any) {
        notify({ level: "error", title: "Error", message: "Failed to checkout container" });
        return rejectWithValue(error?.message || "Failed to checkout container");
    }
});

const calculateOccurredOn = (dateOfUse: Date, timeOfUse: string): string => {
    const [timeStr, period] = timeOfUse.split(" ");
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);

    // Convert to 24 hour format
    const adjustedHour =
        period === "PM" && hour !== 12 ? hour + 12 : period === "AM" && hour === 12 ? 0 : hour;

    return dayjs(dateOfUse)
        .set("hour", adjustedHour)
        .set("minute", parseInt(minutes))
        .toISOString();
};

export const createLogEntries = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>("containerLogDrawer/createLogEntries", async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState().containerLogDrawer;

    if (
        !state.selectedContainer ||
        !state.dateOfUse ||
        !state.timeOfUse ||
        !state.amountRemoved ||
        !state.primarySignee ||
        !state.selectedPatient
    ) {
        return rejectWithValue("Missing required fields for administration");
    }

    if (
        state.showWastingCard &&
        (!state.wastingAmount || !state.wastingReasonOption || !state.wastingMethodOption)
    ) {
        return rejectWithValue("Missing required fields for wasting");
    }

    try {
        const occurredOnISO = calculateOccurredOn(state.dateOfUse, state.timeOfUse);
        const containerLogs: csl.ICreateLogEntry[] = [];

        containerLogs.push({
            containerId: state.selectedContainer.id,
            amount: state.amountRemoved,
            logType: state.isAdministered ? ECSLLogType.ADMINISTERING : ECSLLogType.DISPENSING,
            primaryLogOptionId: state.primaryLogOption?.id,
            secondaryLogOptionId: state.secondaryLogOption?.id,
            accountPatientId: state.selectedPatient.id,
            appointmentId: state.selectedAppointment?.id,
            comments: state.comments,
            occurredOnISO,
        });
        if (state.showWastingCard) {
            containerLogs.push({
                containerId: state.selectedContainer.id,
                amount: state.wastingAmount!,
                logType: ECSLLogType.WASTING,
                primaryLogOptionId: state.wastingReasonOption!.id,
                secondaryLogOptionId: state.wastingMethodOption!.id,
                accountPatientId: state.selectedPatient.id,
                appointmentId: state.selectedAppointment?.id,
                comments: state.wastingComments,
                occurredOnISO,
            });
        }

        const batchPayload: csl.IBatchCreateLogEntry = {
            containerLogs,
            primarySignerId: state.primarySignee.id,
            witnessSignerId: state.witnessSignee?.id,
        };

        // Create logs
        const createLogRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/batch`;
        const response = await axios.post<csl.IContainerLogFullDetails[]>(
            createLogRoute,
            batchPayload,
            { withCredentials: true },
        );

        // Collect all signature requests that have corresponding signature blobs
        const signatureRequests = Object.values(
            response.data
                .flatMap((log) => log.signatureRequests)
                .map((sr) => ({
                    signatureRequestId: sr.id,
                    signatureBlob:
                        sr.signer.id === state.primarySignee?.id
                            ? state.signatureBlob
                            : sr.signer.id === state.witnessSignee?.id
                              ? state.witnessSignatureBlob
                              : undefined,
                    ipAddress: window.location.hostname,
                }))
                .filter((sr) => sr.signatureBlob) // Only include signature requests with blobs
                .filter(
                    (obj1, i, arr) =>
                        arr.findIndex(
                            (obj2) => obj2.signatureRequestId === obj1.signatureRequestId,
                        ) === i,
                ), // Remove duplicates
        );

        // Submit all signatures in one batch if there are any
        if (signatureRequests.length > 0) {
            await axios.post(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/batch-sign`,
                { signatureRequests },
                { withCredentials: true },
            );
        }

        notify({ level: "success", title: "Success", message: "Log entries created successfully" });
        dispatch(
            triggerSearchTableRefetch([
                ESearchTableType.CSL_CURRENT_INVENTORY,
                ESearchTableType.CSL_CONTAINER_LOGS,
            ]),
        );
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.message || error?.message || "Failed to create log entries";
        notify({ level: "error", title: "Error", message: errorMessage });
        return rejectWithValue(errorMessage);
    }
});

export const submitSignatureRequestTask = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/submitSignatureRequestTask",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState().containerLogDrawer;
        if (!state.signatureRequest || (!state.signatureBlob && !state.witnessSignatureBlob)) {
            return rejectWithValue("Missing signature data");
        }
        const blob = state.signatureBlob || state.witnessSignatureBlob;
        try {
            // Build signature request object
            const signatureRequests = [
                {
                    signatureRequestId: state.signatureRequest.id,
                    signatureBlob: blob,
                    ipAddress: window.location.hostname,
                },
            ];
            // Submit signatures
            await axios.post(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/batch-sign`,
                { signatureRequests },
                { withCredentials: true },
            );
            notify({
                level: "success",
                title: "Success",
                message: "Signatures submitted successfully",
            });

            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message || error?.message || "Failed to submit signatures";
            notify({ level: "error", title: "Error", message: errorMessage });
            return rejectWithValue(errorMessage);
        }
    },
);

const containerLogDrawerSlice = createDrawerSlice({
    name: "containerLogDrawer",
    initialState: initialStateContainerLogDrawer,
    reducers: {
        hideContainerLogDrawer: (state) => ({ ...state, show: false }),
        createNewLogEntry: {
            prepare: () => {
                return { payload: { id: generateUUID() } };
            },
            reducer: (state, action: PayloadAction<{ id: string }>) => {
                return { ...initialStateContainerLogDrawer, id: action.payload.id, show: true };
            },
        },
        clearCheckoutStatus: (state) => {
            state.checkoutStatus = undefined;
            state.checkoutMessage = undefined;
            state.checkoutError = null;
        },
        setPatient: (state, action: PayloadAction<patients.IAccountPatientSearch | undefined>) => {
            state.selectedPatient = action.payload;
        },
        setAppointment: (state, action: PayloadAction<IAppointmentLite | undefined>) => {
            state.selectedAppointment = action.payload;
        },
        setMedication: (state, action: PayloadAction<csl.INdcVariantFullDetails | undefined>) => {
            state.selectedMedication = action.payload;
        },
        setContainer: (state, action: PayloadAction<csl.IContainerFullDetails>) => {
            state.selectedContainer = action.payload;
        },
        setDateOfUse: (state, action: PayloadAction<Date | undefined>) => {
            state.dateOfUse = action.payload;
        },
        setTimeOfUse: (state, action: PayloadAction<string>) => {
            state.timeOfUse = action.payload;
        },
        setAmountRemoved: (state, action: PayloadAction<number>) => {
            state.amountRemoved = action.payload;
        },
        setIsAdministered: (state, action: PayloadAction<boolean>) => {
            state.isAdministered = action.payload;
            state.primaryLogOption = undefined;
            state.secondaryLogOption = undefined;
        },
        setPrimaryLogOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.primaryLogOption = action.payload;
        },
        setSecondaryLogOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.secondaryLogOption = action.payload;
        },

        setComments: (state, action: PayloadAction<string>) => {
            state.comments = action.payload;
        },
        setPrimarySignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.primarySignee = action.payload;
            state.signedDateTimeISO = undefined;
            state.signatureBlob = undefined;
        },
        setSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.signatureBlob = action.payload;
            state.signedDateTimeISO = dayjs().toISOString();
        },
        setShowWastingCard: (state, action: PayloadAction<boolean>) => {
            state.showWastingCard = action.payload;
        },
        setWastingAmount: (state, action: PayloadAction<number>) => {
            state.wastingAmount = action.payload;
        },
        setWastingMethodOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.wastingMethodOption = action.payload;
        },
        setWastingReasonOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.wastingReasonOption = action.payload;
        },
        setWastingComments: (state, action: PayloadAction<string>) => {
            state.wastingComments = action.payload;
        },
        setWastingWitnessSignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.witnessSignee = action.payload;
            state.witnessSignedDateTimeISO = undefined;
            state.witnessSignatureBlob = undefined;
        },
        setWastingWitnessSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.witnessSignatureBlob = action.payload;
            state.witnessSignedDateTimeISO = dayjs().toISOString();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(openContainerLogDrawer.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.id = `containerLogDrawer-${action.meta.arg.containerLogId}`;
            })
            .addCase(openContainerLogDrawer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.logDetails = action.payload;
                state.containerLogId = action.payload.id;
            })
            .addCase(openContainerLogDrawer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load log entry details";
            })
            // Checkout container
            .addCase(checkoutContainer.pending, (state) => {
                state.isSubmittingCheckOut = true;
                state.checkoutError = null;
            })
            .addCase(checkoutContainer.fulfilled, (state, action) => {
                state.isSubmittingCheckOut = false;
                state.checkoutStatus = action.payload.checkoutStatus;
                state.checkoutMessage = action.payload.checkoutMessage;

                if (
                    action.payload.checkoutStatus === ECSLCheckoutStatus.SUCCESS ||
                    action.payload.checkoutStatus === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF
                ) {
                    state.selectedContainer = { ...action.payload };
                }
            })
            .addCase(checkoutContainer.rejected, (state, action) => {
                state.isSubmittingCheckOut = false;
                state.checkoutError = action.payload || "Failed to checkout container";
            })
            // Create log entry
            .addCase(createLogEntries.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createLogEntries.fulfilled, (state, action) => {
                state.isLoading = false;
                state.show = false; // Close drawer on success
                notify({
                    level: "success",
                    title: "Success",
                    message: "Log entry created successfully",
                });
            })
            .addCase(createLogEntries.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Start signature request Signing Process
            .addCase(startContainerLogSignature.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.id = `containerLogDrawer-signature-request-id-${action.meta.arg}`;
            })
            .addCase(startContainerLogSignature.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSignatureMode = true;

                // Find the primary signature request from any of the container logs
                const primarySignatureRequest =
                    action.payload.containerLogs[0].signatureRequests.find(
                        (sr) => sr.signatureType === ECSLSignatureRequestType.PRIMARY,
                    );

                if (!primarySignatureRequest) {
                    handleError(new Error("No primary signature request found"));
                    return;
                }
                state.signatureRequest = action.payload.signatureRequest;
                state.isSignatureSubmitted = state.signatureRequest.completedOnISO !== undefined;
                // Find the administering/dispensing log and wasting log
                const adminLog = action.payload.containerLogs.find(
                    (log) =>
                        log.logType === ECSLLogType.ADMINISTERING ||
                        log.logType === ECSLLogType.DISPENSING,
                );
                const wastingLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.WASTING,
                );
                const mainLog = adminLog || wastingLog;

                if (!mainLog) {
                    notify({
                        level: "error",
                        title: "Error",
                        message: "No log entry found for signature request",
                    });
                    handleError(
                        new Error(
                            "No valid log entries found for signature request when starting signing task",
                        ),
                    );
                    return;
                }
                // Set main log data
                state.selectedPatient = mainLog.accountPatient;
                state.selectedAppointment = mainLog.appointment;
                state.selectedContainer = mainLog.container;
                state.selectedMedication = mainLog.ndcVariant;
                state.dateOfUse = new Date(mainLog.occurredOnISO);
                state.timeOfUse = dayjs(mainLog.occurredOnISO).format("hh:mm A");
                state.amountRemoved = Math.abs(mainLog.amount || 0);
                if (adminLog) {
                    state.isAdministered = adminLog.logType === ECSLLogType.ADMINISTERING;
                    state.primaryLogOption = adminLog.primaryLogOption;
                    state.secondaryLogOption = adminLog.secondaryLogOption;
                    state.comments = adminLog.comments;
                }
                if (wastingLog) {
                    state.showWastingCard = true;
                    state.wastingAmount = Math.abs(wastingLog.amount || 0);
                    state.wastingReasonOption = wastingLog.primaryLogOption;
                    state.wastingMethodOption = wastingLog.secondaryLogOption;
                    state.wastingComments = wastingLog.comments;
                    // Find witness signature from wasting log's signature requests
                    const witnessSignatureRequest = wastingLog.signatureRequests.find(
                        (sr) => sr.signatureType === ECSLSignatureRequestType.WITNESS,
                    );
                    if (witnessSignatureRequest) {
                        state.witnessSignee = witnessSignatureRequest.signer;
                        state.witnessSignatureBlob = witnessSignatureRequest.signatureBlob;
                        state.witnessSignedDateTimeISO = witnessSignatureRequest.completedOnISO;
                    }
                }
                // Set primary signature details from primary signature request
                state.primarySignee = primarySignatureRequest.signer;
                state.signedDateTimeISO = primarySignatureRequest.completedOnISO;
                state.signatureBlob = primarySignatureRequest.signatureBlob;
            })
            .addCase(startContainerLogSignature.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load signature request details";
            })
            // Submit signature request task
            .addCase(submitSignatureRequestTask.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitSignatureRequestTask.fulfilled, (state) => {
                return { ...initialStateContainerLogDrawer }; // Reset state and close drawer
            })
            .addCase(submitSignatureRequestTask.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to submit signatures";
            })
            // Initialize log entry with appointment
            .addCase(initializeLogEntryWithAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.id = `containerLogDrawer-${generateUUID()}`;
            })
            .addCase(initializeLogEntryWithAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedAppointment = action.payload.appointment;
                state.selectedPatient = action.payload.patient;
            })
            .addCase(initializeLogEntryWithAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load appointment details";
                state.show = false;
            });
    },
});

export const {
    createNewLogEntry,
    hideContainerLogDrawer,
    setPatient,
    setAppointment,
    setMedication,
    setContainer,
    setDateOfUse,
    setTimeOfUse,
    setAmountRemoved,
    setIsAdministered,
    setPrimaryLogOption,
    setSecondaryLogOption,
    setComments,
    setPrimarySignee,
    setSignatureBlob,
    setShowWastingCard,
    setWastingAmount,
    setWastingMethodOption,
    setWastingReasonOption,
    setWastingComments,
    setWastingWitnessSignee,
    setWastingWitnessSignatureBlob,
} = containerLogDrawerSlice.actions;

export default containerLogDrawerSlice.reducer;
